import { Component, Input } from '@angular/core';
import { NgbActiveModal } from '@ng-bootstrap/ng-bootstrap';
import { NotificacaoService } from 'app/services/notificacao.service';
import { DocumentoService } from 'app/services/documento.service';

@Component({
    selector: 'app-documento-upload',
    templateUrl: './documento-upload.component.html',
    styleUrls: ['./documento-upload.component.scss'],
    standalone: false
})
export class DocumentoUploadComponent {
    @Input() clienteId: number = 0;

    loading: boolean = false;
    arquivoSelecionado: File | null = null;
    observacao: string = '';
    dragOver: boolean = false;

    private readonly tamanhoMaximo = 10 * 1024 * 1024; // 10MB
    private readonly tiposPermitidos = [
        'application/pdf',
        'image/jpeg',
        'image/png',
        'image/gif',
        'image/webp',
        'application/msword',
        'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
        'application/vnd.ms-excel',
        'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
        'text/plain',
        'text/csv'
    ];

    constructor(
        private activeModal: NgbActiveModal,
        private notificacaoService: NotificacaoService,
        private documentoService: DocumentoService
    ) { }

    cancelar() {
        this.activeModal.close(false);
    }

    onDragOver(event: DragEvent) {
        event.preventDefault();
        event.stopPropagation();
        this.dragOver = true;
    }

    onDragLeave(event: DragEvent) {
        event.preventDefault();
        event.stopPropagation();
        this.dragOver = false;
    }

    onDrop(event: DragEvent) {
        event.preventDefault();
        event.stopPropagation();
        this.dragOver = false;

        const files = event.dataTransfer?.files;
        if (files && files.length > 0) {
            this.validarESelecionar(files[0]);
        }
    }

    onFileSelected(event: Event) {
        const input = event.target as HTMLInputElement;
        if (input.files && input.files.length > 0) {
            this.validarESelecionar(input.files[0]);
        }
    }

    private validarESelecionar(arquivo: File) {
        if (arquivo.size > this.tamanhoMaximo) {
            this.notificacaoService.mensagemErro('O arquivo deve ter no máximo 10MB');
            return;
        }

        if (!this.tiposPermitidos.includes(arquivo.type)) {
            this.notificacaoService.mensagemErro('Tipo de arquivo não permitido. Tipos aceitos: PDF, imagens, documentos Office, TXT e CSV');
            return;
        }

        this.arquivoSelecionado = arquivo;
    }

    removerArquivo() {
        this.arquivoSelecionado = null;
    }

    formatarTamanho(bytes: number): string {
        if (bytes < 1024) return bytes + ' B';
        if (bytes < 1024 * 1024) return (bytes / 1024).toFixed(1) + ' KB';
        return (bytes / (1024 * 1024)).toFixed(1) + ' MB';
    }

    obterIconeArquivo(tipo: string): string {
        if (tipo.startsWith('image/')) return 'image';
        if (tipo === 'application/pdf') return 'picture_as_pdf';
        if (tipo.includes('word') || tipo.includes('document')) return 'article';
        if (tipo.includes('excel') || tipo.includes('spreadsheet')) return 'table_chart';
        return 'insert_drive_file';
    }

    async confirmar() {
        if (!this.arquivoSelecionado) {
            this.notificacaoService.mensagemErro('Selecione um arquivo para anexar');
            return;
        }

        this.loading = true;
        try {
            const resposta = await new Promise<any>((resolve, reject) =>
                this.documentoService.adicionar(
                    this.clienteId,
                    this.arquivoSelecionado!,
                    this.observacao?.trim() || undefined
                ).subscribe({ next: resolve, error: reject })
            );
            this.notificacaoService.mensagemSucesso(resposta.mensagem);
            this.activeModal.close(true);
        } catch (err: any) {
            this.notificacaoService.mensagemErro(err?.error?.mensagem || 'Erro ao anexar documento');
        } finally {
            this.loading = false;
        }
    }
}
